/**
 * payslipPDF.js — PDF generation for payslips
 *
 * Builds an A4 PDF entirely from jsPDF drawing primitives (text, lines,
 * rectangles). No screenshot of the UI. html2canvas is imported but not used
 * in the current implementation — reserved for future complex sections.
 *
 * Usage:
 *   const doc = await generatePayslipPDF(worker, payslip, attendance, earnings, deductions, payouts);
 *   doc.save('filename.pdf');          // desktop download
 *   const blob = doc.output('blob');  // for Web Share API
 */

import { jsPDF } from 'jspdf';

// ── Font loading ──────────────────────────────────────────────────────────────

function bufToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let b64 = '';
  // Process in 8KB chunks to avoid call stack limits on large fonts
  for (let i = 0; i < bytes.byteLength; i += 8192) {
    b64 += String.fromCharCode(...bytes.subarray(i, i + 8192));
  }
  return btoa(b64);
}

async function loadInterFonts(doc) {
  try {
    const [regBuf, boldBuf] = await Promise.all([
      fetch('/fonts/Inter-Regular.ttf').then(r => r.arrayBuffer()),
      fetch('/fonts/Inter-Bold.ttf').then(r => r.arrayBuffer()),
    ]);
    doc.addFileToVFS('Inter-Regular.ttf', bufToBase64(regBuf));
    doc.addFont('Inter-Regular.ttf', 'Inter', 'normal');
    doc.addFileToVFS('Inter-Bold.ttf', bufToBase64(boldBuf));
    doc.addFont('Inter-Bold.ttf', 'Inter', 'bold');
    return true;
  } catch {
    return false; // falls back to Helvetica
  }
}

// Active font name — set by generatePayslipPDF after loading Inter, used by helpers
let FONT = 'helvetica';

// ── Page geometry ─────────────────────────────────────────────────────────────

const PW      = 210;   // page width mm
const PH      = 297;   // page height mm
const M       = 15;    // margin mm
const CW      = PW - 2 * M;  // content width = 180mm
const FOOTER_H = 14;   // reserved at bottom for footer

// ── Colour palette ────────────────────────────────────────────────────────────

const C_INK    = [30,  30,  30 ];
const C_MID    = [110, 110, 110];
const C_LIGHT  = [190, 190, 190];
const C_BG     = [248, 248, 248];
const C_GREEN  = [77,  99,  37 ];
const C_RED    = [138, 47,  26 ];

// ── Attendance table column layout (must sum to CW = 180mm) ──────────────────

const COLS = [
  { key: 'date',       label: 'Date',       w: 30, align: 'left'   },
  { key: 'status',     label: 'Status',     w: 20, align: 'left'   },
  { key: 'in',         label: 'In',         w: 14, align: 'center' },
  { key: 'out',        label: 'Out',        w: 14, align: 'center' },
  { key: 'productive', label: 'Productive', w: 22, align: 'center' },
  { key: 'break',      label: 'Break',      w: 16, align: 'center' },
  { key: 'earnings',   label: 'Earnings',   w: 32, align: 'left'   },
  { key: 'deductions', label: 'Deductions', w: 32, align: 'left'   },
];
// 30+20+14+14+22+16+32+32 = 180 ✓

const CELL_PAD_V  = 1.5;  // vertical cell padding mm
const CELL_LINE_H = 2.9;  // height per line of text mm
const CELL_PAD_H  = 1.5;  // horizontal cell padding mm

// ── Standalone formatters (no React / helpers imports) ────────────────────────

function fmtCurrency(amount) {
  const n = parseFloat(amount);
  if (isNaN(n) || amount == null) return '—';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n);
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = String(dateStr).split('-').map(Number);
  if (!y || !m || !d) return String(dateStr);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmtDayLabel(dateStr) {
  const [y, m, d] = String(dateStr).split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const day  = String(d).padStart(2, '0');
  const mon  = date.toLocaleDateString('en-IN', { month: 'short' });
  const wday = date.toLocaleDateString('en-IN', { weekday: 'short' });
  return `${day} ${mon}, ${wday}`;
}

function toIST(utcString) {
  if (!utcString) return '—';
  return new Date(utcString).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function minsToHrs(mins) {
  const n = parseInt(mins, 10);
  if (!n) return '—';
  const h = Math.floor(n / 60);
  const r = n % 60;
  if (h === 0) return `${r}m`;
  return r > 0 ? `${h}h ${r}m` : `${h}h`;
}

function generateDays(start, end) {
  if (!start || !end) return [];
  const days = [];
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  const cur  = new Date(sy, sm - 1, sd);
  const last = new Date(ey, em - 1, ed);
  while (cur <= last) {
    days.push([
      cur.getFullYear(),
      String(cur.getMonth() + 1).padStart(2, '0'),
      String(cur.getDate()).padStart(2, '0'),
    ].join('-'));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

const STATUS_LABEL = {
  present: 'Present', checkout: 'Present', approved: 'Present', break: 'Present',
  absent: 'Absent', weekly_off: 'Weekly Off', leave: 'Leave', unmarked: 'Unmarked',
};

// ── Drawing helpers ───────────────────────────────────────────────────────────

function hRule(doc, y, color = C_LIGHT, lineW = 0.2) {
  doc.setDrawColor(...color);
  doc.setLineWidth(lineW);
  doc.line(M, y, PW - M, y);
  doc.setLineWidth(0.2);
}

function sectionLabel(doc, text, y) {
  doc.setFont(FONT, 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...C_MID);
  doc.text(text, M, y);
  doc.setTextColor(...C_INK);
  return y + 4;
}

function finRow(doc, label, value, y, { bold = false, size = 8.5, color = C_INK } = {}) {
  doc.setFont(FONT, bold ? 'bold' : 'normal');
  doc.setFontSize(size);
  doc.setTextColor(...color);
  doc.text(label, M, y);
  doc.text(value, PW - M, y, { align: 'right' });
  doc.setTextColor(...C_INK);
  return y + (bold ? 5 : 4.5);
}

function drawTableHeader(doc, y) {
  doc.setFillColor(...C_BG);
  doc.rect(M, y, CW, 5.5, 'F');
  let x = M;
  doc.setFont(FONT, 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(...C_MID);
  COLS.forEach(col => {
    const tx = col.align === 'center' ? x + col.w / 2 : x + CELL_PAD_H;
    doc.text(col.label, tx, y + 3.5, { align: col.align === 'center' ? 'center' : 'left' });
    x += col.w;
  });
  doc.setTextColor(...C_INK);
  hRule(doc, y + 5.5);
  return y + 6.5;
}

function addFooters(doc, genDate) {
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C_LIGHT);
    doc.text(
      `Generated by Haziri App · ${genDate}   |   Page ${i} of ${total}`,
      PW / 2, PH - 8, { align: 'center' },
    );
  }
  doc.setTextColor(...C_INK);
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Generate a payslip PDF from the data already loaded on PayslipPage.
 *
 * @param {object} workerData      - worker object (id, name, doj)
 * @param {object} payslipData     - payslip record
 * @param {Array}  attendanceData  - attendance records with expanded punch objects
 * @param {Array}  earningsData    - earnings records
 * @param {Array}  deductionsData  - deductions records
 * @param {Array}  payoutsData     - payout records
 * @returns {jsPDF} doc instance — call .save(filename) or .output('blob')
 */
export async function generatePayslipPDF(
  workerData,
  payslipData,
  attendanceData,
  earningsData,
  deductionsData,
  payoutsData,
) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const interLoaded = await loadInterFonts(doc);
  FONT = interLoaded ? 'Inter' : 'helvetica';
  const SAFE = PH - M - FOOTER_H;
  const genDate = new Date().toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  let y = M;

  function newPage() {
    doc.addPage();
    y = M;
  }

  function guard(needed) {
    if (y + needed > SAFE) newPage();
  }

  // ── Header ──────────────────────────────────────────────────────────────────

  doc.setFont(FONT, 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...C_INK);
  doc.text('INNOMALOUS PET PRODUCTS', M, y);
  y += 6;

  doc.setFont(FONT, 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C_MID);
  doc.text(`Payslip #${workerData?.id}/${payslipData.cycle_label}`, M, y);
  y += 5;

  doc.setFont(FONT, 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...C_INK);
  doc.text(workerData?.name || '—', M, y);
  y += 5;

  doc.setFont(FONT, 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...C_MID);

  const headerMeta = [
    workerData?.doj ? `Joined: ${fmtDate(workerData.doj)}` : null,
    `Period: ${fmtDate(payslipData.cycle_start)} – ${fmtDate(payslipData.cycle_end)}`,
    `Payout Date: ${fmtDate(payslipData.payout_date)}`,
    `Status: ${(payslipData.status || '').charAt(0).toUpperCase() + (payslipData.status || '').slice(1)}`,
  ].filter(Boolean);

  headerMeta.forEach(line => { doc.text(line, M, y); y += 4; });

  y += 2;
  hRule(doc, y);
  y += 6;

  // ── Attendance Summary ───────────────────────────────────────────────────────

  const summary = attendanceData.reduce((acc, r) => {
    const s = r.status;
    if (['checkout','present','break','approved'].includes(s)) acc.present++;
    else if (s === 'absent')     acc.absent++;
    else if (s === 'weekly_off') acc.off++;
    else if (s === 'leave')      acc.leave++;
    else if (s === 'unmarked')   acc.unmarked++;
    const pI = r.punch_in?.timestamp, pO = r.punch_out?.timestamp;
    const bM = r.break_duration || r.breaks?.reduce((s, b) => s + (b.duration || 0), 0) || 0;
    const sM = r.shift_duration || (pI && pO
      ? Math.max(0, Math.round((new Date(pO) - new Date(pI)) / 60000) - bM)
      : 0);
    acc.shiftMins += sM;
    acc.breakMins += bM;
    return acc;
  }, { present: 0, absent: 0, off: 0, leave: 0, unmarked: 0, shiftMins: 0, breakMins: 0 });

  y = sectionLabel(doc, 'ATTENDANCE SUMMARY', y);

  const summaryItems = [
    { label: 'Present',     value: summary.present },
    { label: 'Absent',      value: summary.absent  },
    { label: 'Weekly Off',  value: summary.off     },
    { label: 'Leave',       value: summary.leave   },
    { label: 'Unmarked',    value: summary.unmarked },
    { label: 'Total Worked Hrs', value: `${(summary.shiftMins / 60).toFixed(1)}h` },
  ].filter(item => item.value !== 0 && item.value !== '0.0h');

  if (summaryItems.length > 0) {
    const colW = CW / summaryItems.length;
    summaryItems.forEach((item, i) => {
      const x = M + i * colW;
      doc.setFont(FONT, 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...C_INK);
      doc.text(String(item.value), x, y);
      doc.setFont(FONT, 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...C_MID);
      doc.text(item.label, x, y + 3.5);
    });
    y += 11;
  }

  hRule(doc, y);
  y += 6;

  // ── Attendance Details Table ─────────────────────────────────────────────────

  y = sectionLabel(doc, 'ATTENDANCE DETAILS', y);

  const attMap = {}, ernMap = {}, dedMap = {};
  attendanceData.forEach(r => { if (r.date) attMap[r.date] = r; });
  earningsData.forEach(e => {
    if (!e.date) return;
    (ernMap[e.date] = ernMap[e.date] || []).push(e);
  });
  deductionsData.forEach(d => {
    if (!d.date) return;
    (dedMap[d.date] = dedMap[d.date] || []).push(d);
  });

  // Earnings/deductions can carry a date outside the cycle window (e.g. a
  // final-settlement deduction added after the cycle closed) — union those
  // in the same way the on-screen payslip view does, so rows aren't silently
  // dropped just because they fall outside cycle_start–cycle_end.
  const calendarDays = generateDays(payslipData.cycle_start, payslipData.cycle_end);
  const extraDays = new Set(
    [...earningsData.map(e => e.date), ...deductionsData.map(d => d.date)]
      .filter(d => d && !calendarDays.includes(d))
  );
  const allDays = [...calendarDays, ...Array.from(extraDays).sort()];

  y = drawTableHeader(doc, y);

  allDays.forEach(date => {
    const att    = attMap[date];
    const erns   = ernMap[date] || [];
    const deds   = dedMap[date] || [];
    const status = att?.status || null;
    const isOff  = ['absent', 'weekly_off', 'leave'].includes(status);

    // Build earnings/deductions lines
    // Amount left, type label right — matches on-screen mini-ledger layout
    const ernLines = erns.map(e => `${fmtCurrency(e.amount)}  ${e.type}`);
    if (erns.length > 1) ernLines.push(`${fmtCurrency(erns.reduce((s, e) => s + parseFloat(e.amount || 0), 0))}  total`);
    const dedLines = deds.map(d => `${fmtCurrency(d.amount)}  ${d.type}`);
    if (deds.length > 1) dedLines.push(`${fmtCurrency(deds.reduce((s, d) => s + parseFloat(d.amount || 0), 0))}  total`);

    const maxLines = Math.max(1, ernLines.length, dedLines.length);
    const rowH = CELL_PAD_V + maxLines * CELL_LINE_H + CELL_PAD_V;

    // New page + re-draw header if needed
    if (y + rowH > SAFE) {
      doc.addPage();
      y = M;
      y = drawTableHeader(doc, y);
    }

    const pI = att?.punch_in?.timestamp ?? null;
    const pO = att?.punch_out?.timestamp ?? null;
    const bM = att?.break_duration || att?.breaks?.reduce((s, b) => s + (b.duration || 0), 0) || 0;
    const sM = att?.shift_duration || (pI && pO
      ? Math.max(0, Math.round((new Date(pO) - new Date(pI)) / 60000) - bM)
      : 0);

    const cells = {
      date:       [fmtDayLabel(date)],
      status:     [status ? (STATUS_LABEL[status] || status) : '—'],
      in:         [isOff ? '—' : toIST(pI)],
      out:        [isOff ? '—' : toIST(pO)],
      productive: [isOff ? '—' : minsToHrs(sM)],
      break:      [isOff ? '—' : minsToHrs(bM)],
      earnings:   ernLines.length > 0 ? ernLines : ['—'],
      deductions: dedLines.length > 0 ? dedLines : ['—'],
    };

    doc.setFont(FONT, 'normal');
    doc.setFontSize(7);

    let x = M;
    COLS.forEach(col => {
      const lines = cells[col.key] || ['—'];
      const tx = col.align === 'center' ? x + col.w / 2 : x + CELL_PAD_H;
      const align = col.align === 'center' ? 'center' : 'left';
      const maxW = col.w - CELL_PAD_H * 2;

      if (col.key === 'earnings')   doc.setTextColor(...C_GREEN);
      else if (col.key === 'deductions') doc.setTextColor(...C_RED);
      else doc.setTextColor(...C_INK);

      lines.forEach((line, li) => {
        doc.text(line, tx, y + CELL_PAD_V + li * CELL_LINE_H + 1.5, { align });
      });
      x += col.w;
    });

    doc.setTextColor(...C_INK);
    y += rowH;
    hRule(doc, y, [230, 230, 230]);
  });

  y += 6;

  // ── Financials ───────────────────────────────────────────────────────────────

  guard(80);
  hRule(doc, y);
  y += 6;

  y = sectionLabel(doc, 'FINANCIALS', y);

  // Earnings & Deductions sub-section
  y = sectionLabel(doc, 'EARNINGS & DEDUCTIONS', y);

  const openBal = parseFloat(payslipData.opening_balance || 0);
  if (openBal !== 0) y = finRow(doc, 'Previous Balance', fmtCurrency(openBal), y);
  y = finRow(doc, '+ Total Earnings',    fmtCurrency(payslipData.total_earnings),   y);
  y = finRow(doc, '- Total Deductions', fmtCurrency(payslipData.total_deductions), y);

  hRule(doc, y);
  y += 3;
  y = finRow(doc, 'Gross Payable',
    fmtCurrency(openBal + parseFloat(payslipData.total_earnings || 0) - parseFloat(payslipData.total_deductions || 0)),
    y, { bold: true, size: 9.5 });

  // Payments sub-section
  if (payoutsData.length > 0) {
    y += 3;
    y = sectionLabel(doc, 'PAYMENTS', y);

    payoutsData.forEach(p => {
      guard(5);
      doc.setFont(FONT, 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(...C_INK);
      doc.text(fmtDate(p.date), M, y);
      const mode = p.mode ? p.mode.charAt(0).toUpperCase() + p.mode.slice(1) : '—';
      doc.text(mode, M + 50, y);
      doc.text(fmtCurrency(p.amount), PW - M, y, { align: 'right' });
      y += 4.5;
    });

    hRule(doc, y);
    y += 3;
    y = finRow(doc, 'Total Payments', fmtCurrency(payslipData.total_payouts), y, { bold: true, size: 9.5 });
  }

  // Round-off row (only when non-zero)
  const roundOff = parseFloat(payslipData.roundOff_amount);
  if (!isNaN(roundOff) && Math.abs(roundOff) >= 0.01) {
    y += 2;
    y = finRow(doc, 'Round-off', fmtCurrency(roundOff), y, { color: C_MID });
  }

  // Net Due
  y += 2;
  doc.setDrawColor(...C_INK);
  doc.setLineWidth(0.5);
  doc.line(M, y, PW - M, y);
  doc.setLineWidth(0.2);
  y += 4;
  y = finRow(doc, 'Net Due', fmtCurrency(payslipData.net_due), y, { bold: true, size: 10 });

  // ── Footers on every page ────────────────────────────────────────────────────

  addFooters(doc, genDate);

  return doc;
}
